// Editor für eine einzelne Bedingung (Agent 5A, Runde 5), als Inhalt eines Popovers an der Pille. Jede Änderung gilt sofort
// (die Liste darunter rechnet mit), getippte Werte mit kurzer Verzögerung. „Fertig“, Enter und ein Klick daneben schließen.
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { FilterKatalogFeld, FilterOption } from "../../api/filter";
import { FILTER_TEXTE } from "./filterTexte";
import {
  EINHEIT_AUSWAHL, PERIODEN, TEXT_OPERATOREN, ZAHL_OPERATOREN, ZEIT_OPERATOREN, ZEIT_SCHNELLWAHL, gleicherMoment, zahlMitOperator, zahlOperator,
  zeitOperator, type Bedingung, type Einheit, type FeldKnoten, type Grenzen, type Moment, type TextModus, type VolltextKnoten, type ZahlOperator, type ZeitOperator,
} from "./filterModell";
import { DatumFeld } from "../DatumFeld";

const normal = (s: string) => s.toLocaleLowerCase("de").normalize("NFD").replace(/[̀-ͯ]/g, "");

// ---------------------------------------------------------------------------------------------------------------------------------
// Kleine Bausteine
// ---------------------------------------------------------------------------------------------------------------------------------

/** Eingabe, die ihren Wert erst nach kurzer Ruhe, beim Verlassen, bei Enter und beim Schließen meldet (sonst rechnet jede Taste neu). */
function VerzoegerteEingabe({
  wert, onWert, typ = "text", placeholder, autoFokus, onEnter, ariaLabel, schmal,
}: {
  wert: string;
  onWert: (w: string) => void;
  typ?: "text" | "number";
  placeholder?: string;
  autoFokus?: boolean;
  onEnter?: () => void;
  ariaLabel?: string;
  schmal?: boolean;
}) {
  const [lokal, setLokal] = useState(wert);
  const lokalRef = useRef(wert);
  const gesendet = useRef(wert);
  const timer = useRef<number | undefined>(undefined);
  const melden = useRef(onWert);
  melden.current = onWert;

  useEffect(() => {
    if (wert !== gesendet.current) {
      gesendet.current = wert;
      lokalRef.current = wert;
      setLokal(wert);
    }
  }, [wert]);

  const senden = () => {
    window.clearTimeout(timer.current);
    if (lokalRef.current !== gesendet.current) {
      gesendet.current = lokalRef.current;
      melden.current(lokalRef.current);
    }
  };
  // beim Schließen des Popovers nicht verlieren, was gerade getippt ist
  useEffect(() => () => senden(), []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <input
      className={`eingabe${schmal ? " fe__schmal" : ""}`}
      type={typ}
      value={lokal}
      placeholder={placeholder}
      aria-label={ariaLabel ?? placeholder}
      autoFocus={autoFokus}
      onChange={(e) => {
        lokalRef.current = e.target.value;
        setLokal(e.target.value);
        window.clearTimeout(timer.current);
        timer.current = window.setTimeout(senden, 350);
      }}
      onBlur={senden}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          senden();
          onEnter?.();
        }
      }}
    />
  );
}

function Zeile({ label, children }: { label?: string; children: ReactNode }) {
  return (
    <div className="fe__zeile">
      {label ? <span className="fe__label">{label}</span> : null}
      <div className="fe__feld">{children}</div>
    </div>
  );
}

function Wahl<T extends string>({ wert, optionen, onWahl, ariaLabel }: { wert: T; optionen: { wert: T; label: string }[]; onWahl: (w: T) => void; ariaLabel: string }) {
  return (
    <select className="eingabe" value={wert} aria-label={ariaLabel} onChange={(e) => onWahl(e.target.value as T)}>
      {optionen.map((o) => (
        <option key={o.wert} value={o.wert}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function Umkehren({ negate, onAendern }: { negate: boolean; onAendern: (n: boolean) => void }) {
  return (
    <label className="fe__haken">
      <input type="checkbox" checked={negate} onChange={(e) => onAendern(e.target.checked)} />
      {FILTER_TEXTE.umkehren}
    </label>
  );
}

// ---------------------------------------------------------------------------------------------------------------------------------
// Auswahl (Status, Nutzer, Auswahlfelder, feste Werte)
// ---------------------------------------------------------------------------------------------------------------------------------

function OptionenEditor({ k, info, onAendern }: { k: FeldKnoten; info: FilterKatalogFeld | undefined; onAendern: (k: FeldKnoten) => void }) {
  const b = k.bedingung as Extract<Bedingung, { art: "verweis" | "begriff" }>;
  const werte = b.art === "verweis" ? b.ids : b.werte;
  const optionen: FilterOption[] = info?.optionen ?? [];
  const [suche, setSuche] = useState("");
  const gesetzt = (neu: string[]) => onAendern({ ...k, bedingung: b.art === "verweis" ? { ...b, ids: neu } : { ...b, werte: neu } });
  const umschalten = (w: string) => {
    const neu = new Set(werte);
    if (neu.has(w)) neu.delete(w);
    else neu.add(w);
    // in der Reihenfolge der Auswahl; was nicht (mehr) in der Auswahl steht, bleibt hinten stehen
    gesetzt([...optionen.map((o) => o.wert).filter((x) => neu.has(x)), ...[...neu].filter((x) => !optionen.some((o) => o.wert === x))]);
  };
  const fremde = werte.filter((w) => !optionen.some((o) => o.wert === w));
  const sichtbar = useMemo(() => {
    const n = normal(suche.trim());
    return n ? optionen.filter((o) => normal(o.label).includes(n) || normal(o.unter ?? "").includes(n)) : optionen;
  }, [optionen, suche]);
  const [frei, setFrei] = useState("");

  return (
    <>
      <Zeile>
        <Wahl
          ariaLabel="Operator"
          wert={k.negate ? "nicht" : "ist"}
          optionen={[
            { wert: "ist", label: FILTER_TEXTE.istEiner },
            { wert: "nicht", label: FILTER_TEXTE.istNicht },
          ]}
          onWahl={(w) => onAendern({ ...k, negate: w === "nicht" })}
        />
      </Zeile>
      {optionen.length > 7 ? (
        <input className="eingabe" value={suche} placeholder={FILTER_TEXTE.suchen} aria-label={FILTER_TEXTE.suchen} onChange={(e) => setSuche(e.target.value)} />
      ) : null}
      {optionen.length ? (
        <ul className="fe__optionen" role="listbox" aria-multiselectable="true">
          {sichtbar.map((o) => (
            <li key={o.wert}>
              <label className="fe__option">
                <input type="checkbox" checked={werte.includes(o.wert)} onChange={() => umschalten(o.wert)} />
                {o.farbe !== null ? <span className={`punkt punkt--${o.farbe ?? "gray"}`} aria-hidden="true" /> : null}
                <span className="fe__optiontext">{o.label}</span>
                {o.unter ? <span className="fe__optionunter">{o.unter}</span> : null}
              </label>
            </li>
          ))}
          {fremde.map((w) => (
            <li key={w}>
              <label className="fe__option">
                <input type="checkbox" checked onChange={() => umschalten(w)} />
                <span className="fe__optiontext">{w}</span>
                <span className="fe__optionunter">nicht mehr vorhanden</span>
              </label>
            </li>
          ))}
          {!sichtbar.length && !fremde.length ? <li className="fe__leer">{FILTER_TEXTE.keineOptionen}</li> : null}
        </ul>
      ) : (
        <>
          <div className="fe__chips">
            {werte.map((w) => (
              <button key={w} type="button" className="chip fe__chip" title="Entfernen" onClick={() => gesetzt(werte.filter((x) => x !== w))}>
                {w} ×
              </button>
            ))}
          </div>
          <input
            className="eingabe"
            value={frei}
            placeholder={FILTER_TEXTE.werteEintragen}
            aria-label={FILTER_TEXTE.werteEintragen}
            onChange={(e) => setFrei(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && frei.trim()) {
                e.preventDefault();
                gesetzt([...new Set([...werte, frei.trim()])]);
                setFrei("");
              }
            }}
          />
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------------------------------------------------------------
// Zeit
// ---------------------------------------------------------------------------------------------------------------------------------

type MomentArt = "" | "periode" | "versatz" | "datum" | "jetzt" | "roh";

function heuteIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function MomentEditor({ label, moment, onAendern }: { label: string; moment: Moment | null; onAendern: (m: Moment | null) => void }) {
  const art: MomentArt = moment ? moment.art : "";
  const wechsel = (neu: MomentArt) => {
    if (neu === "") onAendern(null);
    else if (neu === "periode") onAendern({ art: "periode", bereich: "today", which: "start" });
    else if (neu === "versatz") onAendern({ art: "versatz", menge: 7, einheit: "days", richtung: "past", tagesgrenze: "start" });
    else if (neu === "datum") onAendern({ art: "datum", tag: heuteIso(), which: "start" });
    else if (neu === "jetzt") onAendern({ art: "jetzt" });
  };
  const grenzeMoeglich = moment?.art === "versatz" && ["days", "weeks", "months", "years"].includes(moment.einheit);
  return (
    <div className="fe__moment">
      <span className="fe__label">{label}</span>
      <div className="fe__momentfelder">
        <select className="eingabe" value={art} aria-label={`${label}: Art des Zeitpunkts`} onChange={(e) => wechsel(e.target.value as MomentArt)}>
          {art === "" ? <option value="">{FILTER_TEXTE.zeitpunktWaehlen}</option> : null}
          <option value="periode">{FILTER_TEXTE.zeitart.periode}</option>
          <option value="versatz">{FILTER_TEXTE.zeitart.versatz}</option>
          <option value="datum">{FILTER_TEXTE.zeitart.datum}</option>
          <option value="jetzt">{FILTER_TEXTE.zeitart.jetzt}</option>
          {art === "roh" ? <option value="roh">Anderer Zeitpunkt (aus Close)</option> : null}
        </select>
        {moment?.art === "periode" ? (
          <>
            <select className="eingabe" value={moment.bereich} aria-label="Zeitraum" onChange={(e) => onAendern({ ...moment, bereich: e.target.value })}>
              {PERIODEN.map((p) => (
                <option key={p.wert} value={p.wert}>
                  {p.label}
                </option>
              ))}
            </select>
            <Wahl<"start" | "end"> ariaLabel="Beginn oder Ende" wert={moment.which} optionen={[{ wert: "start", label: FILTER_TEXTE.beginn }, { wert: "end", label: FILTER_TEXTE.ende }]} onWahl={(which) => onAendern({ ...moment, which })} />
          </>
        ) : null}
        {moment?.art === "versatz" ? (
          <>
            <Wahl<"past" | "future">
              ariaLabel="Richtung"
              wert={(moment.richtung === "past") === (moment.menge >= 0) ? "past" : "future"}
              optionen={[{ wert: "past", label: FILTER_TEXTE.vor }, { wert: "future", label: FILTER_TEXTE.in }]}
              onWahl={(r) => onAendern({ ...moment, menge: Math.abs(moment.menge), richtung: r })}
            />
            <VerzoegerteEingabe schmal typ="number" ariaLabel="Menge" wert={String(Math.abs(moment.menge))} onWert={(w) => onAendern({ ...moment, menge: Math.abs(Number(w) || 0), richtung: (moment.richtung === "past") === (moment.menge >= 0) ? "past" : "future" })} />
            <Wahl<Einheit> ariaLabel="Einheit" wert={moment.einheit} optionen={EINHEIT_AUSWAHL} onWahl={(einheit) => onAendern({ ...moment, einheit, tagesgrenze: ["days", "weeks", "months", "years"].includes(einheit) ? moment.tagesgrenze : null })} />
            {grenzeMoeglich ? (
              <select className="eingabe" value={moment.tagesgrenze ?? ""} aria-label="Tagesgrenze" onChange={(e) => onAendern({ ...moment, tagesgrenze: (e.target.value || null) as "start" | "end" | null })}>
                <option value="start">auf Tagesbeginn</option>
                <option value="end">auf Tagesende</option>
                <option value="">genau</option>
              </select>
            ) : null}
          </>
        ) : null}
        {moment?.art === "datum" ? (
          <>
            <DatumFeld className="eingabe eingabe--datum" value={moment.tag} aria-label="Datum" onChange={(e) => e.target.value && onAendern({ ...moment, tag: e.target.value })} />
            <Wahl<"start" | "end"> ariaLabel="Beginn oder Ende des Tages" wert={moment.which} optionen={[{ wert: "start", label: "Beginn des Tages" }, { wert: "end", label: "Ende des Tages" }]} onWahl={(which) => onAendern({ ...moment, which })} />
          </>
        ) : null}
      </div>
    </div>
  );
}

function ZeitEditor({ k, onAendern }: { k: FeldKnoten; onAendern: (k: FeldKnoten) => void }) {
  const b = k.bedingung as Extract<Bedingung, { art: "zeitraum" }>;
  const op = zeitOperator(b);
  const setze = (ab: Moment | null, vor: Moment | null) => onAendern({ ...k, bedingung: { art: "zeitraum", ab, vor } });
  const wechselOp = (neu: ZeitOperator) => {
    const ein = b.ab ?? b.vor;
    if (neu === "ab") setze(ein, null);
    else if (neu === "bis") setze(null, ein);
    else setze(b.ab ?? { art: "periode", bereich: "today", which: "start" }, b.vor ?? { art: "jetzt" });
  };
  return (
    <>
      <div className="fe__chips" role="group" aria-label="Schnellwahl">
        {ZEIT_SCHNELLWAHL.map((s) => (
          <button key={s.id} type="button" className={`fe__schnell${gleicherMoment(b.ab, s.ab) && gleicherMoment(b.vor, s.vor) && !k.negate ? " fe__schnell--an" : ""}`} onClick={() => onAendern({ ...k, negate: false, bedingung: { art: "zeitraum", ab: s.ab, vor: s.vor } })}>
            {s.label}
          </button>
        ))}
      </div>
      <Zeile>
        <Wahl<ZeitOperator> ariaLabel="Operator" wert={op} optionen={ZEIT_OPERATOREN} onWahl={wechselOp} />
      </Zeile>
      {op !== "bis" ? <MomentEditor label={FILTER_TEXTE.grenze.ab} moment={b.ab} onAendern={(ab) => setze(ab, b.vor)} /> : null}
      {op !== "ab" ? <MomentEditor label={FILTER_TEXTE.grenze.bis} moment={b.vor} onAendern={(vor) => setze(b.ab, vor)} /> : null}
      <Umkehren negate={k.negate} onAendern={(negate) => onAendern({ ...k, negate })} />
    </>
  );
}

// ---------------------------------------------------------------------------------------------------------------------------------
// Zahl und Dauer
// ---------------------------------------------------------------------------------------------------------------------------------

const SEK_EINHEITEN = [
  { wert: "1", label: FILTER_TEXTE.einheit.sek },
  { wert: "60", label: FILTER_TEXTE.einheit.min },
  { wert: "3600", label: FILTER_TEXTE.einheit.std },
  { wert: "86400", label: FILTER_TEXTE.einheit.tage },
];

function einheitFuer(g: Grenzen): string {
  const werte = [g.gt, g.gte, g.lt, g.lte].filter((w): w is number => w !== null && w !== 0);
  if (!werte.length) return "1";
  for (const f of ["86400", "3600", "60"]) if (werte.every((w) => w % Number(f) === 0)) return f;
  return "1";
}

function ZahlEditor({ k, info, onAendern }: { k: FeldKnoten; info: FilterKatalogFeld | undefined; onAendern: (k: FeldKnoten) => void }) {
  const b = k.bedingung as Extract<Bedingung, { art: "zahl" | "dauer" }>;
  const sekunden = b.art === "dauer" || info?.art === "dauer";
  const op = zahlOperator(b);
  const [faktor, setFaktor] = useState(() => (sekunden ? einheitFuer(b) : "1"));
  const f = Number(faktor);
  const setzen = (g: Grenzen) => onAendern({ ...k, bedingung: { ...b, ...g } });
  const anzeige = (w: number | null) => (w === null ? "" : String(Math.round((w / f) * 1000) / 1000));
  const lesen = (t: string): number | null => (t.trim() === "" || !Number.isFinite(Number(t)) ? null : Number(t) * f);
  const feld = (name: keyof Grenzen, label?: string) => (
    <VerzoegerteEingabe schmal typ="number" ariaLabel={label ?? "Wert"} wert={anzeige(b[name])} onWert={(t) => setzen({ ...b, [name]: lesen(t) })} />
  );
  return (
    <>
      <Zeile>
        <Wahl<ZahlOperator>
          ariaLabel="Operator"
          wert={op}
          optionen={op === "frei" ? [...ZAHL_OPERATOREN, { wert: "frei", label: "Bereich (aus Close)" }] : ZAHL_OPERATOREN}
          onWahl={(neu) => setzen(zahlMitOperator(b, neu))}
        />
      </Zeile>
      {op === "frei" ? (
        <div className="fe__frei">
          <Zeile label="größer als">{feld("gt", "größer als")}</Zeile>
          <Zeile label="mindestens">{feld("gte", "mindestens")}</Zeile>
          <Zeile label="kleiner als">{feld("lt", "kleiner als")}</Zeile>
          <Zeile label="höchstens">{feld("lte", "höchstens")}</Zeile>
        </div>
      ) : (
        <div className="fe__momentfelder">
          {feld(op === "kleiner" ? "lt" : op === "hoechstens" ? "lte" : op === "groesser" ? "gt" : "gte")}
          {op === "zwischen" ? (
            <>
              <span className="fe__und">und</span>
              {feld("lte", "bis")}
            </>
          ) : null}
          {sekunden ? <Wahl ariaLabel="Einheit" wert={faktor} optionen={SEK_EINHEITEN} onWahl={setFaktor} /> : info?.einheit ? <span className="fe__einheit">{info.einheit}</span> : null}
        </div>
      )}
      <Umkehren negate={k.negate} onAendern={(negate) => onAendern({ ...k, negate })} />
    </>
  );
}

// ---------------------------------------------------------------------------------------------------------------------------------
// Text
// ---------------------------------------------------------------------------------------------------------------------------------

function TextEditor({ modus, wert, negate, onAendern, onEnter }: { modus: TextModus; wert: string; negate: boolean; onAendern: (n: { modus: TextModus; wert: string; negate: boolean }) => void; onEnter: () => void }) {
  return (
    <>
      <Zeile>
        <Wahl<TextModus> ariaLabel="Operator" wert={modus} optionen={TEXT_OPERATOREN} onWahl={(m) => onAendern({ modus: m, wert, negate })} />
      </Zeile>
      <VerzoegerteEingabe autoFokus ariaLabel="Text" placeholder="Text …" wert={wert} onWert={(w) => onAendern({ modus, wert: w, negate })} onEnter={onEnter} />
      <Umkehren negate={negate} onAendern={(n) => onAendern({ modus, wert, negate: n })} />
    </>
  );
}

// ---------------------------------------------------------------------------------------------------------------------------------
// Zusammenbau
// ---------------------------------------------------------------------------------------------------------------------------------

interface Props {
  knoten: FeldKnoten | VolltextKnoten;
  info: FilterKatalogFeld | undefined;
  feldName: string;
  onAendern: (k: FeldKnoten | VolltextKnoten) => void;
  onEntfernen: () => void;
  onFertig: () => void;
}

export function BedingungEditor({ knoten: k, info, feldName, onAendern, onEntfernen, onFertig }: Props) {
  const wurzel = useRef<HTMLDivElement>(null);
  // Enter außerhalb von Eingaben (z. B. auf einem Haken) schließt nicht; in Texteingaben schließt es über onEnter
  let inhalt: ReactNode;
  if (k.typ === "volltext") {
    inhalt = <TextEditor modus={k.modus} wert={k.wert} negate={k.negate} onAendern={(n) => onAendern({ ...k, ...n })} onEnter={onFertig} />;
  } else {
    const b = k.bedingung;
    switch (b.art) {
      case "verweis":
      case "begriff":
        inhalt = <OptionenEditor k={k} info={info} onAendern={onAendern} />;
        break;
      case "zeitraum":
        inhalt = <ZeitEditor k={k} onAendern={onAendern} />;
        break;
      case "zahl":
      case "dauer":
        inhalt = <ZahlEditor k={k} info={info} onAendern={onAendern} />;
        break;
      case "text":
        inhalt = <TextEditor modus={b.modus} wert={b.wert} negate={k.negate} onAendern={(n) => onAendern({ ...k, negate: n.negate, bedingung: { art: "text", modus: n.modus, wert: n.wert } })} onEnter={onFertig} />;
        break;
      case "existiert":
        inhalt = (
          <Zeile>
            <Wahl ariaLabel="Operator" wert={k.negate ? "leer" : "gesetzt"} optionen={[{ wert: "gesetzt", label: FILTER_TEXTE.istGesetzt }, { wert: "leer", label: FILTER_TEXTE.istLeer }]} onWahl={(w) => onAendern({ ...k, negate: w === "leer" })} />
          </Zeile>
        );
        break;
      default:
        inhalt = <p className="fe__hinweis">{FILTER_TEXTE.unbekannteBedingung}</p>;
    }
  }
  return (
    <div className="fe" ref={wurzel}>
      <div className="fe__kopf">{feldName}</div>
      <div className="fe__inhalt">{inhalt}</div>
      <div className="fe__fuss">
        <button type="button" className="knopf knopf--leise knopf--klein fe__entfernen" onClick={onEntfernen}>
          {FILTER_TEXTE.entfernen}
        </button>
        <button type="button" className="knopf knopf--primaer knopf--klein" onClick={onFertig}>
          {FILTER_TEXTE.fertig}
        </button>
      </div>
    </div>
  );
}
